import type { ComponentProps, ComponentPropsWithoutRef, ElementType } from 'react';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  Box,
  Divider,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@mui/material';

import Tooltip from '@/shared/ui/primitives/Tooltip';
import Typography from '@/shared/ui/typography/Text';

import type { ExtraProps } from 'react-markdown';
import type { Sx } from '@/types/helpers';

const linkSx: Sx = {
  textDecoration: 'none',
  display: 'inline-flex',
  alignItems: 'center'
};
const chevronSx: Sx = { fontSize: '1em' };
const h1Sx: Sx = { fontSize: '20px', mt: 3, mb: 1.5, fontWeight: 700 };
const h2Sx: Sx = { fontSize: '16px', mt: 3, mb: 1.5, fontWeight: 700 };
const h3Sx: Sx = { fontSize: '14px', mt: 3, mb: 1.5, fontWeight: 700 };
const h5Sx: Sx = { fontSize: '13px', mt: 3, mb: 1.5, fontWeight: 700 };
const h6Sx: Sx = { fontSize: '12px', mt: 3, mb: 1.5, fontWeight: 700 };
const blockquoteSx: Sx = {
  borderLeft: '4px solid',
  borderColor: 'divider',
  pl: 2,
  my: 1.5,
  mx: 0,
  color: 'gx.textSecondary'
};
const codeSx: Sx = {
  backgroundColor: 'action.hover',
  borderRadius: '4px',
  px: 0.5,
  py: 0.25,
  fontSize: '13px',
  fontFamily: 'monospace'
};
const preSx: Sx = {
  backgroundColor: 'action.hover',
  borderRadius: '4px',
  p: 2,
  my: 1.5,
  overflow: 'auto',
  fontSize: '13px',
  fontFamily: 'monospace'
};
const hrSx: Sx = { my: 3 };
const delSx: Sx = { textDecoration: 'line-through' };
const imgSx: Sx = { maxWidth: '100%', height: 'auto', display: 'block', my: 1.5 };
const listSx: Sx = { pl: 3, my: 1.5 };
const liSx: Sx = { fontSize: '14px', mb: 0.5 };
const pSx: Sx = { fontSize: '14px', my: 1.5, lineHeight: 1.7 };
const strongSx: Sx = { fontWeight: 700 };
const emSx: Sx = { fontStyle: 'italic' };
const tableCellBodySx: Sx = {
  border: '0.5px solid white',
  px: 1.5,
  py: 1,
  fontSize: '14px'
};
const tableCellHeaderSx: Sx = {
  border: '0.5px solid white',
  px: 1.5,
  py: 1,
  fontSize: '14px',
  fontWeight: 700
};

export const MarkdownLink: ElementType<ComponentProps<'a'> & ExtraProps> = ({
  node,
  children,
  ...props
}) => (
  <Tooltip
    title='Click to see index information'
    variant='secondary'
  >
    <Link
      {...props}
      color='primary'
      rel='noopener noreferrer'
      sx={linkSx}
      target='_blank'
    >
      {children}
      <ChevronRightIcon sx={chevronSx} />
    </Link>
  </Tooltip>
);

export const MarkdownH1: ElementType<ComponentProps<'h1'> & ExtraProps> = ({ node, ...props }) => (
  <Typography
    sx={h1Sx}
    variant='h1'
    {...props}
  />
);

export const MarkdownH2: ElementType<ComponentProps<'h2'> & ExtraProps> = ({ node, ...props }) => (
  <Typography
    sx={h2Sx}
    variant='h2'
    {...props}
  />
);

export const MarkdownH3: ElementType<ComponentProps<'h3'> & ExtraProps> = ({ node, ...props }) => (
  <Typography
    sx={h3Sx}
    variant='h3'
    {...props}
  />
);

export const MarkdownH4: ElementType<ComponentProps<'h4'> & ExtraProps> = ({ node, ...props }) => (
  <Typography
    sx={h3Sx}
    variant='h4'
    {...props}
  />
);

export const MarkdownH5: ElementType<ComponentProps<'h5'> & ExtraProps> = ({ node, ...props }) => (
  <Typography
    sx={h5Sx}
    variant='h5'
    {...props}
  />
);

export const MarkdownH6: ElementType<ComponentProps<'h6'> & ExtraProps> = ({ node, ...props }) => (
  <Typography
    sx={h6Sx}
    variant='h6'
    {...props}
  />
);

export const MarkdownBlockquote: ElementType<ComponentProps<'blockquote'> & ExtraProps> = ({
  node,
  ...props
}) => (
  <Box
    {...props}
    component='blockquote'
    sx={blockquoteSx}
  />
);

export const MarkdownCode: ElementType<ComponentProps<'code'> & ExtraProps> = ({
  node,
  ...props
}) => (
  <Box
    {...props}
    component='code'
    sx={codeSx}
  />
);

export const MarkdownPre: ElementType<ComponentProps<'pre'> & ExtraProps> = ({
  node,
  ...props
}) => (
  <Box
    {...props}
    component='pre'
    sx={preSx}
  />
);

export const MarkdownHr: ElementType<ComponentProps<'hr'> & ExtraProps> = ({ node, ...props }) => (
  <Divider
    {...props}
    sx={hrSx}
  />
);

export const MarkdownDel: ElementType<ComponentProps<'del'> & ExtraProps> = ({
  node,
  ...props
}) => (
  <Box
    {...props}
    component='del'
    sx={delSx}
  />
);

export const MarkdownTable: ElementType<ComponentProps<'table'> & ExtraProps> = ({
  node,
  ...props
}) => <Table {...props} />;

export const MarkdownThead: ElementType<ComponentProps<'thead'> & ExtraProps> = ({
  node,
  ...props
}) => <TableHead {...props} />;

export const MarkdownTbody: ElementType<ComponentProps<'tbody'> & ExtraProps> = ({
  node,
  ...props
}) => <TableBody {...props} />;

export const MarkdownTr: ElementType<ComponentProps<'tr'> & ExtraProps> = ({ node, ...props }) => (
  <TableRow {...props} />
);

export const MarkdownImg: ElementType<ComponentProps<'img'> & ExtraProps> = ({
  node,
  ...props
}) => (
  <Box
    {...props}
    component='img'
    sx={imgSx}
  />
);

export const MarkdownUl: ElementType<ComponentProps<'ul'> & ExtraProps> = ({ node, ...props }) => (
  <Box
    {...props}
    component='ul'
    sx={listSx}
  />
);

export const MarkdownOl: ElementType<ComponentProps<'ol'> & ExtraProps> = ({ node, ...props }) => (
  <Box
    {...props}
    component='ol'
    sx={listSx}
  />
);

export const MarkdownLi: ElementType<ComponentProps<'li'> & ExtraProps> = ({ node, ...props }) => (
  <Typography
    {...props}
    component='li'
    sx={liSx}
  />
);

export const MarkdownP: ElementType<ComponentProps<'p'> & ExtraProps> = ({ node, ...props }) => (
  <Typography
    {...props}
    component='p'
    sx={pSx}
  />
);

export const MarkdownStrong: ElementType<ComponentProps<'strong'> & ExtraProps> = ({
  node,
  ...props
}) => (
  <Box
    {...props}
    component='strong'
    sx={strongSx}
  />
);

export const MarkdownEm: ElementType<ComponentProps<'em'> & ExtraProps> = ({ node, ...props }) => (
  <Box
    {...props}
    component='em'
    sx={emSx}
  />
);

const spanClassToColor: Record<string, string> = {
  muted: 'gx.textDisabled',
  error: 'gx.failure',
  primary: 'gx.highlight',
  secondary: 'gx.textSecondary'
};

export const MarkdownSpan: ElementType<ComponentProps<'span'> & ExtraProps> = ({
  node,
  className,
  ...props
}) => {
  const color = className ? spanClassToColor[className] : undefined;
  return (
    <Box
      {...props}
      component='span'
      sx={color ? { color } : undefined}
    />
  );
};

export const MarkdownTableCell: ElementType<
  (ComponentProps<'th'> | ComponentProps<'td'>) & ExtraProps
> = ({ node, ...props }) => {
  const isHeader = node?.tagName === 'th';
  const mappedProps: ComponentPropsWithoutRef<typeof TableCell> = {
    ...props,
    align: props.align === 'char' ? 'inherit' : props.align
  };
  return (
    <TableCell
      align={isHeader ? 'center' : 'left'}
      sx={isHeader ? tableCellHeaderSx : tableCellBodySx}
      {...mappedProps}
    />
  );
};
